import { useState } from "react";
import CheckMark from "static/svg/check";
import { ErrorMessage } from "components/ErrorMessage";

import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { StripeService } from "@adfenix/types";

import { HqSaveCardStripeForm } from "./HqSaveCardStripeForm";
import { HqRemoveOrChangeStripeCard } from "./HqRemoveOrChangeStripeCard";
import { Box, Button, P, Spinner, Stack } from "@adfenix/alexandria";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { StripeServiceApi } from "api/services";

export interface hqStripeCardHandlerProps {
  hqId: string;
  creditCardsInfo?: StripeService.CustomerCardPaymentMethodsDescription[];
  loadingCardInfo: boolean;
  handleCancel: () => void;
  updateCardInfo: () => void;
  viewCardsMode?: boolean;
  isCardListEmpty?: boolean;
}

export const HqStripeCardHandler = ({
  creditCardsInfo,
  hqId,
  handleCancel,
  updateCardInfo,
  loadingCardInfo,
  viewCardsMode,
  isCardListEmpty
}: hqStripeCardHandlerProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [showAddNewCard, setShowAddNewCard] = useState(false);
  const {
    customer: {
      authV1CustomerCreateCreditCardSetupSessionCreate: getStripeKeys
    }
  } = StripeServiceApi;

  const {
    isInitialLoading: loadingKeys,
    data: stripeKeys,
    refetch
  } = useQuery(["getStripeKeys"], getStripeKeys);

  if (loadingKeys || loadingCardInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Spinner size={50} />
      </Box>
    );
  }

  return (
    <Stack spacing="m">
      {!isCardListEmpty && <P bold>{t("billing_added_cards_card_title")}</P>}

      {creditCardsInfo !== undefined && creditCardsInfo.length > 0 ? (
        <Stack spacing="gutter">
          {creditCardsInfo.map((card, index) => (
            <HqRemoveOrChangeStripeCard
              hqId={hqId}
              viewCardsMode={viewCardsMode}
              key={index}
              cardInfo={card}
              onRemove={() => {
                toast.success(
                  <>
                    <CheckMark
                      style={{ marginRight: 8 }}
                      width={24}
                      color="#fff"
                    />
                    <span>{t("billing_credit_card_successfully_removed")}</span>
                  </>
                );

                refetch(); // We need a fresh key when you remove a card
                updateCardInfo();
                queryClient.invalidateQueries(["getHqStripePaymentMethods"]);
              }}
              onDefaultCardChange={() => {
                toast.success(
                  <>
                    <CheckMark
                      style={{ marginRight: 8 }}
                      width={24}
                      color="#fff"
                    />
                    <span>{t("billing_credit_card_successfully_changed")}</span>
                  </>
                );

                updateCardInfo();
                queryClient.invalidateQueries(["getHqStripePaymentMethods"]);
              }}
            />
          ))}

          {stripeKeys?.publishableKey &&
          stripeKeys?.clientSecret &&
          showAddNewCard ? (
            <>
              <Elements
                stripe={loadStripe(stripeKeys.publishableKey)}
                options={{ clientSecret: stripeKeys.clientSecret }}
              >
                <HqSaveCardStripeForm
                  isCardListEmpty={isCardListEmpty}
                  hqId={hqId}
                  handleCancel={() => setShowAddNewCard(false)}
                  onSuccess={() => {
                    toast.success(
                      <>
                        <CheckMark
                          style={{ marginRight: 8 }}
                          width={24}
                          color="#fff"
                        />
                        <span>
                          {t("billing_credit_card_successfully_added")}
                        </span>
                      </>
                    );

                    updateCardInfo();

                    setShowAddNewCard(false);
                  }}
                  clientSecret={stripeKeys.clientSecret}
                />
              </Elements>
            </>
          ) : null}
        </Stack>
      ) : stripeKeys?.publishableKey && stripeKeys?.clientSecret ? (
        <Elements
          stripe={loadStripe(stripeKeys.publishableKey)}
          options={{ clientSecret: stripeKeys.clientSecret }}
        >
          <HqSaveCardStripeForm
            isCardListEmpty={isCardListEmpty}
            hqId={hqId}
            handleCancel={handleCancel}
            onSuccess={() => {
              toast.success(
                <>
                  <CheckMark
                    style={{ marginRight: 8 }}
                    width={24}
                    color="#fff"
                  />
                  <span>{t("billing_credit_card_successfully_added")}</span>
                </>
              );

              updateCardInfo();
            }}
            clientSecret={stripeKeys.clientSecret}
          />
        </Elements>
      ) : (
        <ErrorMessage
          title={t("order.error.generic.title")}
          description={t("order.error.generic.description")}
        />
      )}

      {creditCardsInfo !== undefined &&
        creditCardsInfo.length > 0 &&
        !showAddNewCard &&
        !viewCardsMode && (
          <Button
            logLabel="add-new-card-button"
            variant="text"
            onClick={() => setShowAddNewCard(true)}
          >
            <i className="uil uil-plus" />
            {t("billing_add_credit_card_button_title")}
          </Button>
        )}
    </Stack>
  );
};
